import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0F6CBD',
      light: '#3B8FD4',
      dark: '#0854A0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#64748B',
      light: '#94A3B8',
      dark: '#475569',
      contrastText: '#ffffff',
    },
    success: {
      main: '#16A34A',
      light: '#22C55E',
      dark: '#15803D',
      contrastText: '#ffffff',
    },
    error: {
      main: '#DC2626',
      light: '#EF4444',
      dark: '#B91C1C',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
      contrastText: '#ffffff',
    },
    info: {
      main: '#3B82F6',
      light: '#60A5FA',
      dark: '#2563EB',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F1F5F9',
      paper: '#FFFFFF',
      subtle: '#F8FAFC',
    },
    text: {
      primary: '#0F172A',
      secondary: '#475569',
      disabled: '#94A3B8',
    },
    divider: '#E2E8F0',
    action: {
      hover: 'rgba(15, 108, 189, 0.04)',
      selected: 'rgba(15, 108, 189, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 12,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: '2.2rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.8rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.3rem',
      fontWeight: 700,
      lineHeight: 1.35,
    },
    h5: {
      fontSize: '1.1rem',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: 1.4,
      letterSpacing: '0.02em',
      color: '#0F172A',
    },
    subtitle1: {
      fontSize: '0.9rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#1E293B',
    },
    subtitle2: {
      fontSize: '0.8rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#475569',
    },
    body1: {
      fontSize: '0.9rem',
      lineHeight: 1.6,
      color: '#0F172A',
    },
    body2: {
      fontSize: '0.8rem',
      lineHeight: 1.6,
      color: '#475569',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.8rem',
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.7rem',
      lineHeight: 1.5,
      color: '#94A3B8',
    },
    overline: {
      fontSize: '0.7rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: '#64748B',
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
  
  // ============================================
  // CUSTOM DASHBOARD SPACING SETTINGS
  // ============================================
  dashboard: {
    sidebar: {
      width: 210,
      paddingTop: 0.5,
      paddingBottom: 1,
      paddingX: 0,
    },
    content: {
      padding: 2.5,
    },
    paper: {
      borderRadius: 2,
      minHeight: 'calc(100vh - 75px)',
      marginTop: 0.2,
    },
    // SECTION HEADING STYLES (Bigger than menu items)
    sectionHeading: {
      fontSize: '0.7rem', // Bigger than menu items (0.78rem for menu)
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: '#64748B',
      paddingX: 2.5,
      paddingY: 0.4,
      marginTop: 0.4,
    },
    // MENU ITEM STYLES (Smaller than section headings)
    navItem: {
      marginBottom: 0.15,
      paddingY: 0.3,
      borderRadius: '6px',
      paddingX: 0.8,
      iconMinWidth: 32,
      primaryFontSize: '0.78rem', // Slightly smaller than section heading
      secondaryFontSize: '0.6rem',
    },
    divider: {
      marginY: 0.3,
      marginX: 2,
    },
  },
  
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollBehavior: 'smooth',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E2E8F0',
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 20px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        sizeSmall: {
          padding: '4px 12px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '1rem',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #0F6CBD 0%, #0854A0 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1A7AD4 0%, #0A5CAD 100%)',
          },
        },
        containedSuccess: {
          background: 'linear-gradient(135deg, #16A34A 0%, #15803D 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
          },
        },
        containedError: {
          background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #E2E8F0',
          transition: 'box-shadow 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          },
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.04)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          borderBottom: '1px solid #E2E8F0',
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#F8FAFC',
          color: '#0F172A',
          borderBottom: '2px solid #E2E8F0',
          fontSize: '0.8125rem',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        },
        body: {
          color: '#0F172A',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(15, 108, 189, 0.04)',
          },
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: 6,
            height: 6,
          },
          '&::-webkit-scrollbar-track': {
            background: '#F1F5F9',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#CBD5E1',
            borderRadius: 3,
            '&:hover': {
              background: '#94A3B8',
            },
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #E2E8F0',
          padding: '8px 16px',
        },
        select: {
          borderRadius: 6,
          padding: '4px 8px',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#FFFFFF',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0F6CBD',
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0F6CBD',
                borderWidth: 2,
              },
            },
          },
          '& .MuiInputLabel-root': {
            fontWeight: 500,
            color: '#475569',
            '&.Mui-focused': {
              color: '#0F6CBD',
            },
          },
        },
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
          fontSize: '0.75rem',
          transition: 'all 0.15s ease-in-out',
          '&:hover': {
            transform: 'scale(1.02)',
          },
        },
        colorSuccess: {
          backgroundColor: '#DCFCE7',
          color: '#15803D',
        },
        colorError: {
          backgroundColor: '#FEE2E2',
          color: '#B91C1C',
        },
        colorWarning: {
          backgroundColor: '#FEF3C7',
          color: '#D97706',
        },
        colorInfo: {
          backgroundColor: '#DBEAFE',
          color: '#2563EB',
        },
        colorPrimary: {
          backgroundColor: '#DBEAFE',
          color: '#0F6CBD',
        },
        colorSecondary: {
          backgroundColor: '#F1F5F9',
          color: '#475569',
        },
        sizeSmall: {
          height: 24,
          fontSize: '0.6875rem',
        },
        sizeMedium: {
          height: 32,
          fontSize: '0.8125rem',
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          fontWeight: 700,
          letterSpacing: '0.05em',
          fontSize: '0.6875rem',
          color: '#64748B',
          textTransform: 'uppercase',
          padding: '8px 16px',
          marginTop: 4,
          lineHeight: 1.5,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          padding: '8px 16px',
          transition: 'all 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(15, 108, 189, 0.06)',
            transform: 'translateX(2px)',
          },
          '&.Mui-selected': {
            backgroundColor: '#0F6CBD',
            color: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(15, 108, 189, 0.3)',
            '&:hover': {
              backgroundColor: '#0854A0',
              transform: 'translateX(2px)',
            },
            '& .MuiListItemIcon-root': {
              color: '#FFFFFF',
            },
            '& .MuiListItemText-primary': {
              color: '#FFFFFF',
              fontWeight: 600,
            },
          },
          '& .MuiListItemText-primary': {
            fontSize: '0.8125rem',
            fontWeight: 500,
          },
          '& .MuiListItemIcon-root': {
            minWidth: 36,
            color: '#64748B',
            transition: 'color 0.15s ease-in-out',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
          color: '#64748B',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '2px 4px',
          padding: '8px 16px',
          '&:hover': {
            backgroundColor: 'rgba(15, 108, 189, 0.06)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(15, 108, 189, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(15, 108, 189, 0.15)',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 500,
          '& .MuiAlert-icon': {
            fontSize: 20,
          },
        },
        standardSuccess: {
          backgroundColor: '#F0FDF4',
          color: '#15803D',
        },
        standardError: {
          backgroundColor: '#FEF2F2',
          color: '#B91C1C',
        },
        standardWarning: {
          backgroundColor: '#FFFBEB',
          color: '#D97706',
        },
        standardInfo: {
          backgroundColor: '#EFF6FF',
          color: '#2563EB',
        },
        filledSuccess: {
          backgroundColor: '#16A34A',
        },
        filledError: {
          backgroundColor: '#DC2626',
        },
        filledWarning: {
          backgroundColor: '#F59E0B',
        },
        filledInfo: {
          backgroundColor: '#3B82F6',
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiAlert-root': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            borderRadius: 12,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#4F6D8A',
          color: '#FFFFFF !important',
          '& *': {
            color: '#FFFFFF !important',
          },
          '& strong': {
            color: '#FFD54F !important',
          },
          '& b': {
            color: '#FFD54F !important',
          },
        },
        arrow: {
          color: '#4F6D8A',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E2E8F0',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E2E8F0',
        },
        indicator: {
          backgroundColor: '#0F6CBD',
          height: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '12px 20px',
          '&.Mui-selected': {
            color: '#0F6CBD',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #E2E8F0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          transition: 'box-shadow 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '16px 20px',
          borderBottom: '1px solid #E2E8F0',
        },
        title: {
          fontSize: '1rem',
          fontWeight: 600,
          color: '#0F172A',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px',
          '&:last-child': {
            paddingBottom: 20,
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '12px 20px',
          borderTop: '1px solid #E2E8F0',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px 16px',
          fontSize: '1.125rem',
          fontWeight: 700,
          color: '#0F172A',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          color: '#475569',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 20px',
          gap: 8,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: 20,
          transition: 'color 0.15s ease-in-out',
        },
        fontSizeSmall: {
          fontSize: 16,
        },
        fontSizeLarge: {
          fontSize: 24,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: 8,
          '&:hover': {
            backgroundColor: 'rgba(15, 108, 189, 0.04)',
          },
        },
        sizeSmall: {
          padding: 4,
        },
        sizeLarge: {
          padding: 12,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        colorDefault: {
          backgroundColor: '#0F6CBD',
          color: '#FFFFFF',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#0F6CBD',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiFormLabel-root': {
            color: '#475569',
            '&.Mui-focused': {
              color: '#0F6CBD',
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          color: '#64748B',
          marginTop: 4,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#0F6CBD',
            '&:hover': {
              backgroundColor: 'rgba(15, 108, 189, 0.08)',
            },
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#0F6CBD',
          },
          '& .MuiSwitch-track': {
            borderRadius: 12,
          },
          '& .MuiSwitch-thumb': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#0F6CBD',
          },
          '&:hover': {
            backgroundColor: 'rgba(15, 108, 189, 0.04)',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#0F6CBD',
          },
          '&:hover': {
            backgroundColor: 'rgba(15, 108, 189, 0.04)',
          },
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            borderRadius: 8,
            '&.Mui-selected': {
              backgroundColor: '#0F6CBD',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#0854A0',
              },
            },
          },
        },
      },
    },
    MuiSpeedDial: {
      styleOverrides: {
        fab: {
          backgroundColor: '#0F6CBD',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#0854A0',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0F6CBD',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#0F6CBD',
              borderWidth: 2,
            },
          },
        },
        option: {
          borderRadius: 6,
          margin: '2px 4px',
          '&:hover': {
            backgroundColor: 'rgba(15, 108, 189, 0.06)',
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'rgba(15, 108, 189, 0.1)',
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        rectangular: {
          borderRadius: 8,
        },
      },
    },
  },
});

export default theme;